import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@chainlit/react-client';
import getRouterBasename from '@/lib/router';

/**
 * Component to handle authentication redirects inside Router context
 * This prevents Safari cookie issues by using React Router navigation
 * instead of window.location.href
 */
export default function AuthRedirect() {
  const { isAuthenticated, isReady } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttempted = useRef(false);

  useEffect(() => {
    // Reset redirect flag when authentication state changes
    if (isAuthenticated) {
      redirectAttempted.current = false;
      return;
    }

    // Only redirect if ready, not authenticated, and not already on an allowed page
    if (!isReady || isAuthenticated) {
      return;
    }

    const currentPath = location.pathname;
    const basename = getRouterBasename();
    const allowedPaths = [
      basename + '/signup',
      basename + '/login',
      basename + '/login/callback',
      basename + '/payment',
      basename + '/payment-success',
    ];

    const isAllowedPath = allowedPaths.some(
      path => currentPath === path || currentPath.startsWith(path)
    );

    if (!isAllowedPath && !redirectAttempted.current) {
      redirectAttempted.current = true;
      // Use navigate instead of window.location.href to avoid full page reload
      // This prevents Safari cookie issues and redirect loops
      navigate('/login', { replace: true });
    }
  }, [isReady, isAuthenticated, location.pathname, navigate]);

  return null;
}
